import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

const toMessage = (v: unknown): string | undefined => {
    if (typeof v === 'string') return v;
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    return undefined;
};

/**
 * Extracts a human-readable message from:
 * - RTK Query fetchBaseQuery errors
 * - SerializedError / plain Error
 * - custom backend payloads
 */
export const getApiErrorMessage = (error: unknown): string => {
    if (!error) return 'Unknown error';

    // Native Error
    if (error instanceof Error) {
        return error.message || 'Unknown error';
    }

    // RTK Query: FetchBaseQueryError
    if (isObject(error) && 'status' in error) {
        const fbe = error as FetchBaseQueryError;

        // status can be number or string ('FETCH_ERROR', 'PARSING_ERROR', ...)
        const status = (fbe as any).status;
        const data = (fbe as any).data;

        // Common backend shapes: {message}, {error}, {details}, {errors:[...]}
        if (typeof data === 'string' && data.trim()) return data;
        if (isObject(data)) {
            const msg =
                toMessage(data.message) ??
                toMessage(data.error) ??
                toMessage(data.details) ??
                toMessage(data.title);
            if (msg && msg.trim()) return msg;

            // Errors array (Laravel-ish etc.)
            if (Array.isArray((data as any).errors)) {
                const first = (data as any).errors[0];
                const firstMsg =
                    toMessage(first) ?? (isObject(first) ? toMessage(first.message) : undefined);
                if (firstMsg && firstMsg.trim()) return firstMsg;
            }

            // Fallback: stringify safely
            try {
                const json = JSON.stringify(data);
                if (json && json !== '{}' && json !== '[]') return json;
            } catch {
                // ignore
            }
        }

        // Some errors provide { error: '...' } at top-level
        const topLevel = isObject(error) ? toMessage((error as any).error) : undefined;
        if (topLevel && topLevel.trim()) return topLevel;

        if (typeof status === 'number') return `Request failed (${status})`;
        if (typeof status === 'string') return status;

        return 'Request failed';
    }

    // SerializedError from RTK / Redux
    if (isObject(error)) {
        const msg = toMessage((error as any).message) ?? toMessage((error as any).error);
        if (msg && msg.trim()) return msg;
        try {
            const json = JSON.stringify(error);
            if (json && json !== '{}' && json !== '[]') return json;
        } catch {
            // ignore
        }
    }

    return 'Unknown error';
};
